/* eslint-disable warp-drive/no-legacy-request-patterns */
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ApplicationStatus from 'linced-app/enums/application-status';
import CommentModerationStatus from '@linc-technologies/linced-common/enums/comment-moderation-status';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PageType from 'linced-app/enums/page-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import moment from 'moment';
import { isEmpty } from '@ember/utils';
import {
  isMobile,
  isTWA,
} from '@linc-technologies/linced-common/utils/platform';

export default class ApplicationDataService extends Service {
  @service
  dates;

  @service
  features;

  @service
  options;

  @service
  permissions;

  @service
  policyAcceptance;

  @service
  pushNotifications;

  @service
  session;

  @service
  store;

  @service
  students;

  // Props
  @tracked
  optionGroups = [];

  @tracked
  staffPages = [];
  @tracked
  schoolPages = [];
  @tracked
  sickbayPage = null;
  @tracked
  unprocessedEnrolmentApplications = [];
  @tracked
  enrolmentsPendingUpload = [];
  @tracked
  events = [];
  @tracked
  sickbayVisits = [];
  @tracked
  incidents = [];
  @tracked
  suspensions = [];
  @tracked
  expulsions = [];
  @tracked
  batches = [];
  @tracked
  medications = [];
  @tracked
  unmoderatedComments = [];

  // This is commented out because we do this up front in the application route atm.
  // If we ever want to lazy load, uncomment this
  /*async init() {
    this._super(...arguments);

    // load all application data after we have loaded user data and have auth
    // Note: session.ensure() is intentionally not called here — startup.js
    // guarantees auth is set up before model() is called for authenticated routes.
    await this.load();
  },*/

  // load warms the store and populates all properties for the given school.
  // beforeModel and model hooks were rolled into here when base route
  async load() {
    let schoolId = this.session.schoolId;
    if (isEmpty(schoolId)) return;

    // Only load policies (currently NZ-only) if the school is NZ jurisdiction.
    if (this.session.school?.jurisdiction === Jurisdiction.NZ_MOE) {
      this.policyAcceptance.loadPolicies();
    }

    // Warm the store in parallel before reading from it below
    await Promise.all([
      this.store.query('person', { type: PersonType.STAFF }),
      this.store.query('group', { schoolId, year: moment().format('YYYY') }),
      this.store.query('posts/page', { schoolId }),
      this.store.query('timetable/structure', { schoolId, include: 'periods' }),
      this.store.query('milestones/milestone', { schoolId }),
      this.store.query('posts/tag-category', { schoolId }),
      this.store.query('posts/tag', { schoolId }),
      this.store.query('user', {
        allowedTenantAccess: schoolId,
        scopesUnion:
          'urn:linced:organisation:role:staff,urn:linced:organisation:role:admin',
      }),
    ]);

    let userIsAdmin = this.permissions.meet(Scopes.TENANT_ADMIN);

    let pages = this.store.peekAll('posts/page');
    this.optionGroups = this.store.peekAll('school-options/group') || [];
    this.staffPages = (pages || []).filter(
      (page) => page.type === PageType.STAFF,
    );

    this.schoolPages = (pages || [])
      .filter((page) => page.type === PageType.SCHOOL)
      .filter((page) => page.visibleInMenu === true);

    this.sickbayPage =
      (pages || []).find((page) => page.isSickbay === true) || null;

    // Admin vs teacher data
    if (userIsAdmin) {
      this.unprocessedEnrolmentApplications = await this.store.query(
        'enrolments/application',
        {
          status: ApplicationStatus.UNPROCESSED,
        },
      );
      this.enrolmentsPendingUpload = await this.students.loadPendingUploads();
    } else {
      this.events = await this.store.query('bookings/event', {
        teacherId: this.session.personId || null,
      });
    }

    // Sickbay visits
    let sickbayForTeachers =
      this.options.value('core:staffPermissions:sickbayForTeachers') || false;
    if (this.features.heroSuiteEnabled && (userIsAdmin || sickbayForTeachers)) {
      let date = moment().format('YYYY-MM-DD');
      this.sickbayVisits = await this.store.query('attendance/sickbay-visit', {
        date,
        current: true,
      });
    }

    // AU NSW admin-specific attendance/medical data
    if (
      this.session.school?.jurisdiction === Jurisdiction.AU_NSW &&
      userIsAdmin
    ) {
      let startOfYear = moment().startOf('year');
      let endOfYear = moment().endOf('year');

      this.incidents = await this.store.query('attendance/behaviour-incident', {
        dateFrom: startOfYear.unix(),
        dateTo: endOfYear.unix(),
        schoolId,
      });

      this.suspensions = await this.store.query('attendance/suspension', {
        dateFrom: startOfYear.format('YYYY-MM-DD'),
        dateTo: endOfYear.format('YYYY-MM-DD'),
        schoolId,
      });

      this.expulsions = await this.store.query('attendance/expulsion', {
        dateFrom: startOfYear.format('YYYY-MM-DD'),
        dateTo: endOfYear.format('YYYY-MM-DD'),
        schoolId,
      });

      this.batches = await this.store.findAll('medical/batch');
      this.medications = await this.store.findAll('medical/medication');
    }

    this.attachDeviceNotifications();

    this.refreshCounts();
  }

  async refreshUnmoderatedComments() {
    let userIsAdmin = this.permissions.meet(Scopes.TENANT_ADMIN);
    let postParams = {
      include: 'comments',
      startTime: (moment(this.dates.rolloverDate).isValid()
        ? moment(this.dates.rolloverDate)
        : moment()
      ).unix(),
      metaOnly: true,
      moderationStatus: JSON.stringify([CommentModerationStatus.UNSPECIFIED]),
    };
    if (!userIsAdmin) postParams.authorId = this.session.personId || null;

    let communityPosts = await this.store.query(
      'posts/communitynotice',
      postParams,
    );
    let postIds = (communityPosts || []).map((post) => post.id);

    this.unmoderatedComments = (this.store.peekAll('posts/comment') || [])
      .filter(
        (comment) =>
          comment.moderationStatus === CommentModerationStatus.UNSPECIFIED,
      )
      .filter((comment) => postIds.includes(comment.get('post.id')));
  }

  attachDeviceNotifications() {
    if (isMobile() || isTWA()) {
      this.pushNotifications.attachDevice(this.session.personId);
    }
  }

  async refreshCounts() {
    if (this.permissions.meet(Scopes.TENANT_ADMIN)) {
      // Update the pending upload counts for tenant admins.
      // Non-admin staff don't have access to the bulk upload.
      this.students?.updatePendingUploads();
      // Update unprocessed enrolment application count.
      this.unprocessedEnrolmentApplications.update();
    }
    // Refresh unmoderated comment count (applies to all staff).
    await this.refreshUnmoderatedComments();
  }
}
